import React, { ReactNode, useContext, useMemo, useRef } from 'react';

import noop from 'lodash/noop';

import { MessagesList } from './blocks/MessagesList';
import { MessageTypes, PREDEFINED_CONFIGS } from './constants';
import { MessageApi, MessageConfig, ContextHolderRef } from './types';

const MessageContext = React.createContext<MessageApi | null>(null);

export const MessageContextProvider = ({ children }: { children: ReactNode }) => {
  const [api, contextHolder] = useInternalMessage();

  return (
    <MessageContext.Provider value={api}>
      {contextHolder}
      {children}
    </MessageContext.Provider>
  );
};

export const useMessage = () => {
  const messageApi = useContext(MessageContext);

  if (!__CLIENT__) {
    return {
      open: noop,
      close: noop,
      destroy: noop,
      ...Object.keys(PREDEFINED_CONFIGS).reduce((acc, key) => ({ ...acc, [key]: noop }), {}),
    } as MessageApi;
  }

  if (messageApi === null) {
    throw new Error(
      'Missing MessageContextProvider in rendered components hierarchy. ' +
        'To fix that, please add MessageContextProvider as parent in component tree.' +
        'For example in App.tsx: ' +
        'export default () => <MessageContextProvider><AppRoutes /></MessageContextProvider>'
    );
  }

  return messageApi;
};

let uniqueKey = 0;

const useInternalMessage = () => {
  const holderApiRef = useRef<ContextHolderRef>();
  const contextHolder = <MessagesList ref={holderApiRef} />;

  const api = useMemo<MessageApi>(() => {
    const open: MessageApi['open'] = (content, config = {}) => {
      let key = config.key;

      if (key === undefined) {
        key = uniqueKey;
        uniqueKey++;
      }

      holderApiRef.current?.open({
        ...config,
        key,
        content,
      });
    };

    const close: MessageApi['close'] = (key) => {
      holderApiRef.current?.close(key);
    };

    const destroy: MessageApi['destroy'] = () => {
      holderApiRef.current?.destroy();
    };

    const predefinedMessagesApi = Object.values(MessageTypes).reduce((acc, type) => {
      acc[type] = (content: ReactNode, config: MessageConfig = {}) =>
        open(content, { ...PREDEFINED_CONFIGS[type], ...config });

      return acc;
    }, {} as Omit<MessageApi, 'close' | 'open' | 'destroy'>);

    return {
      open,
      close,
      destroy,
      ...predefinedMessagesApi,
    };
  }, []);

  return [api, contextHolder] as const;
};
